import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/constants/site";

export default function InfoSections() {
  const { t } = useTranslation();

  const popularItems = t("popular.items", { returnObjects: true }) as string[];
  const whyItems = t("why.items", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];
  const howSteps = t("how.steps", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];

  const whatCards = [
    { icon: "ri-search-line", title: t("what.card1Title"), desc: t("what.card1Desc") },
    { icon: "ri-store-line", title: t("what.card2Title"), desc: t("what.card2Desc") },
    { icon: "ri-shield-check-line", title: t("what.card3Title"), desc: t("what.card3Desc") },
    { icon: "ri-truck-line", title: t("what.card4Title"), desc: t("what.card4Desc") },
  ];

  const whyIcons = ["ri-stack-line", "ri-lightbulb-line", "ri-eye-line", "ri-global-line"];

  return (
    <>
      {/* What is */}
      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-6 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              {t("what.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t("what.subtitle")}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {whatCards.map((card) => (
              <div
                key={card.title}
                className="rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${card.icon} text-xl leading-none`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{card.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-9 text-center">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {t("what.cta")}
              <i className="ri-arrow-right-line flex h-4 w-4 items-center justify-center text-base leading-none" />
            </a>
          </div>
        </div>
      </section>

      {/* Popular searches */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-6 md:py-16">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              {t("popular.title")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 md:text-base">{t("popular.subtitle")}</p>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {popularItems.map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-secondary-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700"
              >
                <i className="ri-search-line flex h-4 w-4 items-center justify-center text-secondary-500" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why use this site */}
      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-6 md:py-16">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              {t("why.title")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 md:text-base">{t("why.subtitle")}</p>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {whyItems.map((item, i) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${whyIcons[i % whyIcons.length]} text-xl leading-none`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-6 md:py-16">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              {t("how.title")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 md:text-base">{t("how.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {howSteps.map((step, i) => (
              <div key={step.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                  {i + 1}
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}